import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBackend, useDirtyState, useParams, useUser } from '@access-evo/evo-api';
import { ToastContainer } from 'react-toastify';
import ChatbotConfig from './legacy/Components/ChatbotConfig/index';
import { AdminLoader } from './components/admin/AdminLoader';
import { getRequestHeaders, setRequestContext } from './evo/runtime';
import { resolveDevSession, type DevSession } from './evo/devAuth';
import { fetchEvoContext, type EvoContext } from './evo/context';

type ChatobiParams = {
  propertyId?: string;
  groupId?: string;
  /**
   * Application Subscription Id of the Evo menu entry the user opened. An
   * organisation can hold several chatbot subscriptions, one chatbot each, so
   * the API needs it to resolve the right property.
   */
  instanceId?: string;
};

export function App() {
  const params = useParams<ChatobiParams>();
  const user = useUser();
  const { getBaseUrl, getRequestHeaders: hostRequestHeaders } = useBackend();
  const [, setDirtyState] = useDirtyState();
  const [backendReady, setBackendReady] = useState(false);
  const [backendError, setBackendError] = useState('');
  const [devSession, setDevSession] = useState<DevSession | null>(null);
  const [evoContext, setEvoContext] = useState<EvoContext | null>(null);
  const paramPropertyId = params.propertyId ?? null;
  const paramGroupId = params.groupId ?? null;
  // 'default' is what the static host uses when the URL names no instance.
  const instanceId =
    params.instanceId && params.instanceId !== 'default' ? params.instanceId : null;

  useEffect(() => {
    let cancelled = false;

    getBaseUrl()
      .then(async (baseUrl) => {
        const normalisedBaseUrl = baseUrl.replace(/\/$/, '');
        const session = await resolveDevSession(normalisedBaseUrl);
        if (cancelled) return;
        setRequestContext({
          baseUrl: normalisedBaseUrl,
          getHeaders: session?.getHeaders ?? hostRequestHeaders,
          onUnauthorised: () =>
            setBackendError('Your session has expired. Please reload SwiftAssist to continue.'),
        });
        setDevSession(session);

        const hasProperty =
          paramPropertyId ?? paramGroupId ?? session?.propertyId ?? session?.groupId;

        if (!hasProperty) {
          const context = await fetchEvoContext(normalisedBaseUrl, getRequestHeaders, instanceId);
          if (cancelled) return;
          // Pin the rest of the session to the subscription the API resolved:
          // the data routes resolve the property per request and would
          // otherwise fall back to the organisation's first chatbot.
          setRequestContext({ instanceId: context?.subscriptionId ?? instanceId });
          setEvoContext(context);
        }

        setBackendReady(true);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setBackendError(error instanceof Error ? error.message : 'Unable to reach the ChatOBI API.');
      });

    return () => {
      cancelled = true;
    };
  }, [getBaseUrl, hostRequestHeaders, paramPropertyId, paramGroupId, instanceId]);

  const feedbackUser = useMemo(
    () => ({
      userId: user?.id ?? '',
      roleId: user?.userTier ?? '',
      customerName: user?.organisation?.name ?? '',
    }),
    [user],
  );

  const handleDirtyChange = useCallback(
    (isDirty: boolean) => setDirtyState(isDirty),
    [setDirtyState],
  );

  if (backendError) {
    return (
      <div className="p-4">
        <h3>Error</h3>
        <h6>{backendError}</h6>
      </div>
    );
  }

  if (!backendReady) {
    return (
      <AdminLoader />
    );
  }

  return (
    <>
      <ChatbotConfig
        propertyId={paramPropertyId ?? devSession?.propertyId ?? evoContext?.propertyId ?? null}
        groupId={paramGroupId ?? devSession?.groupId ?? evoContext?.groupId ?? null}
        evoResolved={Boolean(evoContext)}
        defaultPropertyName={evoContext?.instanceName ?? null}
        user={feedbackUser}
        onDirtyChange={handleDirtyChange}
      />
      <ToastContainer position="top-center" autoClose={4000} />
    </>
  );
}
